/**
 * NovaVerse — central site configuration.
 *
 * This is the single source of truth for branding, navigation and route names.
 * Future feature areas are declared here (as `plannedFeatures`) so the rest of
 * the app can be wired up incrementally WITHOUT hard-coding strings everywhere.
 *
 * NOTE: `plannedFeatures` are intentionally NOT rendered as navigation yet —
 * they only become active as each feature is built in later steps.
 */

export interface NavItem {
  label: string
  href: string
}

export interface PlannedFeature {
  key: string
  label: string
  description: string
  /** Route reserved for this feature. Not yet implemented. */
  route: string
}

export const siteConfig = {
  name: 'NovaVerse',
  tagline: 'Explore Science. Understand Everything.',
  creator: 'Anuj Singh',
  description:
    'NovaVerse is an interactive educational universe to explore science through NCERT content, 3D models, virtual labs, simulations and quizzes.',

  /** Routes that actually exist at this stage. */
  routes: {
    home: '/',
    classSelection: '/class-selection',
  },

  /**
   * Navigation shown in the header.
   * Only includes destinations that truly exist right now.
   */
  primaryNav: [] as NavItem[],

  /** Primary call-to-action on the landing page. */
  primaryCta: {
    label: 'Start Learning',
    href: '/class-selection',
  },

  /**
   * Roadmap of future capabilities. Declared for a scalable foundation.
   * These are placeholders for planning only — do NOT treat as live features.
   */
  plannedFeatures: [
    {
      key: 'ncert',
      label: 'NCERT Content',
      description: 'Structured, syllabus-aligned chapters and concepts.',
      route: '/chapters',
    },
    {
      key: 'models3d',
      label: '3D Models',
      description: 'Interactive 3D models to visualise scientific structures.',
      route: '/lab-3d',
    },
    {
      key: 'virtual-labs',
      label: 'Virtual Labs',
      description: 'Hands-on experiments in a safe, simulated environment.',
      route: '/virtual-labs',
    },
    {
      key: 'simulations',
      label: 'Simulations',
      description: 'Dynamic simulations that bring physics and chemistry to life.',
      route: '/simulations',
    },
    {
      key: 'quiz',
      label: 'Quizzes',
      description: 'Adaptive quizzes to test and reinforce understanding.',
      route: '/quiz',
    },
    {
      key: 'progress',
      label: 'Progress',
      description: 'Personal learning progress and achievements.',
      route: '/progress',
    },
    {
      key: 'auth',
      label: 'Authentication',
      description: 'Secure accounts for learners and educators.',
      route: '/login',
    },
    {
      key: 'admin',
      label: 'Admin Panel',
      description: 'Content and platform management for administrators.',
      route: '/admin',
    },
  ] as PlannedFeature[],
} as const

export type SiteConfig = typeof siteConfig
